import { createFormControl, useFormState } from 'react-hook-form';
import { useFormCoordinatorContext } from './FormCoordinatorContext.tsx';

// Fallback condiviso: useFormState richiede un control non-null, ma il form
// target potrebbe non essere ancora montato. Il singleton evita di creare
// un'istanza RHF per-componente (come faceva `useForm().control`).
// Nessun side-effect: `disabled: true` impedisce le subscription.
const FALLBACK_CONTROL = createFormControl().control;

/**
 * Stato RHF completo (`dirtyFields`, `errors`, …) di un campo o sezione
 * all'interno di un form coordinato.
 *
 * Sottoscrive direttamente il `control` RHF del form target: il parametro
 * `name` attiva il filtraggio per-campo del proxy RHF, così il componente
 * ri-renderizza solo quando lo stato di quel campo specifico cambia.
 *
 * Usare questo hook quando serve sapere **dove** un form è dirty/invalido
 * (quale campo, quale sezione). Consumer tipici: badge sezione, anchor
 * state, errori per-sezione.
 *
 * Per semplici check booleani (`isDirty`, `isValid`) a livello di form
 * intero, usare `useCoordinatorForms` — più leggero, nessun accesso
 * diretto al control RHF.
 */
export function useCoordinatedFormState(props: {
  name: string | undefined;
  formId: string;
}) {
  const { name, formId } = props;
  const coordinator = useFormCoordinatorContext();

  const formRef = coordinator.formRefs.current.get(formId)?.ref.current;

  return useFormState({
    name,
    control: formRef?.control ?? FALLBACK_CONTROL,
    disabled: !formRef?.control,
  });
}
